<template>
  <div>
    <b-card no-body class="mb-2">
      <b-card-header>
        <h4 class="mb-0">جستجو و فیلتر تصاویر پرسنل (هوش مصنوعی)</h4>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col md="4" sm="6" class="mb-1">
            <b-form-group label="واحد سازمانی">
              <b-overlay :show="loadingDropdowns" spinner-small>
                <v-select
                  v-model="filters.org_id"
                  :options="organizations"
                  label="name"
                  :reduce="(org) => org.id"
                  placeholder="انتخاب واحد سازمانی..."
                  dir="rtl"
                />
              </b-overlay>
            </b-form-group>
          </b-col>

          <b-col md="4" sm="6" class="mb-1">
            <b-form-group label="مدیران">
              <b-overlay :show="loadingDropdowns" spinner-small>
                <v-select
                  v-model="filters.manager_ids"
                  multiple
                  :close-on-select="false"
                  :options="managers"
                  label="name"
                  :reduce="(manager) => manager.id"
                  placeholder="انتخاب مدیر..."
                  dir="rtl"
                />
              </b-overlay>
            </b-form-group>
          </b-col>

          <b-col md="4" sm="6" class="mb-1">
            <b-form-group label="دوربین‌ها">
              <b-overlay :show="loadingDropdowns" spinner-small>
                <v-select
                  v-model="filters.camera_ids"
                  multiple
                  :close-on-select="false"
                  :options="cameras"
                  label="name"
                  :reduce="(camera) => camera.id"
                  placeholder="انتخاب دوربین..."
                  dir="rtl"
                />
              </b-overlay>
            </b-form-group>
          </b-col>

          <b-col md="4" sm="6" class="mb-1">
            <b-form-group label="پرسنل (کد پرسنلی)">
              <b-overlay :show="loadingDropdowns" spinner-small>
                <v-select
                  v-model="filters.personal_numbers"
                  multiple
                  :close-on-select="false"
                  :options="users"
                  :getOptionLabel="
                    (user) =>
                      user.personnel_no
                        ? `${user.name} - ${user.personnel_no}`
                        : user.name
                  "
                  :reduce="(user) => user.personnel_no"
                  placeholder="انتخاب پرسنل..."
                  dir="rtl"
                />
              </b-overlay>
            </b-form-group>
          </b-col>

          <b-col md="4" sm="6" class="mb-1">
            <b-form-group label="تاریخ و زمان شروع">
              <date-picker
                v-model="filters.start_date"
                type="datetime"
                format="YYYY-MM-DD HH:mm:ss"
                display-format="jYYYY/jMM/jDD HH:mm"
                placeholder="انتخاب زمان"
                clearable
              />
            </b-form-group>
          </b-col>

          <b-col md="4" sm="6" class="mb-1">
            <b-form-group label="تاریخ و زمان پایان">
              <date-picker
                v-model="filters.end_date"
                type="datetime"
                format="YYYY-MM-DD HH:mm:ss"
                display-format="jYYYY/jMM/jDD HH:mm"
                placeholder="انتخاب زمان"
                clearable
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" class="mt-1 d-flex flex-column">
            <b-alert
              variant="danger"
              :show="!!errorMessage"
              class="p-1 mb-2 d-flex align-items-center w-100"
            >
              <feather-icon icon="AlertCircleIcon" class="mr-50" />
              <span class="align-middle">{{ errorMessage }}</span>
            </b-alert>

            <div class="d-flex justify-content-end align-items-center w-100">
              <b-button
                variant="outline-secondary"
                class="mr-1"
                @click="resetFilters"
              >
                پاک کردن فیلترها
              </b-button>
              <b-button
                variant="primary"
                @click="triggerNewSearch"
                :disabled="loadingImages"
              >
                <b-spinner v-if="loadingImages" small class="mr-50" />
                جستجو و نمایش
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-header v-if="hasSearched && totalRows > 0">
        <h4 class="mb-0">تصاویر یافت شده ({{ totalRows }} مورد)</h4>
      </b-card-header>

      <b-card-body>
        <div v-if="loadingImages" class="text-center py-5">
          <b-spinner variant="primary" />
          <p class="text-muted mt-1">در حال دریافت اطلاعات...</p>
        </div>

        <div v-else-if="images.length > 0">
          <b-row>
            <b-col
              md="4"
              sm="6"
              cols="12"
              v-for="(person, index) in images"
              :key="index"
              class="mb-2"
            >
              <b-card class="text-center h-100 shadow-none border mb-0">
                <div
                  class="mb-1 w-100 d-flex justify-content-center overflow-hidden rounded cursor-pointer hover-zoom"
                  @click="openImageModal(person)"
                >
                  <img
                    v-if="person.base64"
                    :src="`data:image/jpeg;base64,${person.base64}`"
                    class="img-fluid w-100 h-auto object-fit-contain"
                    alt="تصویر پرسنل"
                  />
                  <div
                    v-else
                    class="bg-light w-100 h-100 d-flex align-items-center justify-content-center text-muted"
                  >
                    <feather-icon icon="UserIcon" size="40" />
                  </div>
                </div>

                <div class="d-flex justify-content-between">
                  <h5 class="mb-25 text-truncate">
                    {{ person.first_name || "" }}
                    {{ person.last_name || "نامشخص" }}
                  </h5>
                  <small class="text-muted d-block mb-50 text-truncate">
                    {{ person.company_name || "بدون واحد" }}
                  </small>
                </div>
                <div class="d-flex justify-content-between mt-1">
                  <small class="text-secondary d-block mb-25" dir="ltr">
                    <feather-icon icon="ClockIcon" size="12" class="mr-25" />
                    {{ formatEpochTime(person.time_epoch_ms) }}
                  </small>

                  <small class="text-primary d-block mb-1">
                    <feather-icon icon="VideoIcon" size="12" class="mr-25" />
                    {{ person.camera_name }}
                  </small>

                  <b-badge variant="light-primary">
                    کد: {{ person.melli_code || "ندارد" }}
                  </b-badge>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row class="mt-2" v-if="totalRows > perPage">
            <b-col cols="12" class="d-flex justify-content-center">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0"
              />
            </b-col>
          </b-row>
        </div>

        <div
          v-else-if="hasSearched && !loadingImages && !errorMessage"
          class="text-center py-5"
        >
          <h5 class="text-muted">
            هیچ تصویری با فیلترهای انتخاب‌شده یافت نشد.
          </h5>
        </div>

        <div v-else-if="!hasSearched && !errorMessage" class="text-center py-5">
          <h5 class="text-muted">
            جهت نمایش تصاویر، فیلترهای مورد نظر را انتخاب کرده و جستجو کنید.
          </h5>
        </div>
      </b-card-body>
    </b-card>

    <b-modal
      id="image-modal"
      v-model="isImageModalOpen"
      hide-footer
      hide-header
      centered
      size="md"
      body-class="p-0 bg-transparent"
      content-class="bg-transparent border-0"
    >
      <div class="position-relative text-center">
        <b-button
          variant="danger"
          class="btn-icon rounded-circle position-absolute shadow"
          style="top: 10px; right: 10px; z-index: 10"
          @click="isImageModalOpen = false"
        >
          <feather-icon icon="XIcon" size="20" />
        </b-button>

        <img
          v-if="selectedPersonForModal && selectedPersonForModal.base64"
          :src="`data:image/jpeg;base64,${selectedPersonForModal.base64}`"
          class="img-fluid rounded shadow-lg w-100"
          style="max-height: 80vh; object-fit: contain"
          alt="تصویر پرسنل"
        />

        <div v-else class="bg-white p-5 rounded shadow text-muted">
          <feather-icon icon="ImageIcon" size="50" class="mb-1" />
          <h5>تصویری ثبت نشده است</h5>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BSpinner,
  BBadge,
  BOverlay,
  BPagination,
  BModal,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";
import aiCameraApi from "@api/ai-camera";

export default {
  name: "PersonnelGallery",
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    BBadge,
    BOverlay,
    BPagination,
    BModal,
    BAlert,
    vSelect,
  },
  data() {
    return {
      loadingDropdowns: false,
      loadingImages: false,
      hasSearched: false,
      errorMessage: null,

      organizations: [],
      managers: [],
      users: [],
      cameras: [],
      images: [],

      isImageModalOpen: false,
      selectedPersonForModal: null,

      currentPage: 1,
      totalRows: 0,
      perPage: 9,

      filters: {
        org_id: null,
        manager_ids: [],
        personal_numbers: [],
        camera_ids: [],
        start_date: "",
        end_date: "",
      },
    };
  },
  mounted() {
    this.setDefaultDates();
    this.loadDropdownData();
  },
  watch: {
    currentPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchImages();
      }
    },
  },
  methods: {
    setDefaultDates() {
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, "0");

      const datePart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate(),
      )}`;
      const timePart = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
        now.getSeconds(),
      )}`;

      this.filters.start_date = `${datePart} 00:00:00`;
      this.filters.end_date = `${datePart} ${timePart}`;
    },

    openImageModal(person) {
      if (!person.base64) return;
      this.selectedPersonForModal = person;
      this.isImageModalOpen = true;
    },

    formatEpochTime(epochMs) {
      if (!epochMs) return "نامشخص";
      const date = new Date(parseInt(epochMs));
      return new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(date);
    },

    async loadDropdownData() {
      this.loadingDropdowns = true;
      try {
        const [orgRes, managerRes, userRes, cameraRes] = await Promise.all([
          aiCameraApi.getOrganizations(),
          aiCameraApi.getManagers(),
          aiCameraApi.getUsers(),
          aiCameraApi.getCameras(),
        ]);

        this.organizations = orgRes.data.organizations || [];
        this.managers = managerRes.data.data || [];
        this.users = userRes.data.data || [];
        this.cameras = cameraRes.data.data || cameraRes.data || [];
      } catch (error) {
        console.error("Error loading dropdown options:", error);
      } finally {
        this.loadingDropdowns = false;
      }
    },

    triggerNewSearch() {
      this.currentPage = 1;
      this.fetchImages();
    },

    async fetchImages() {
      this.loadingImages = true;
      this.hasSearched = true;
      this.errorMessage = null;
      this.images = [];
      this.totalRows = 0;

      try {
        const response = await aiCameraApi.getPersonnelImages(
          this.filters,
          this.currentPage,
          this.perPage,
        );

        this.images = response.data.data || [];

        if (response.data.pagination) {
          this.totalRows = Number(response.data.pagination.count) || 0;
          if (response.data.pagination.per_page) {
            this.perPage = Number(response.data.pagination.per_page);
          }
        }
      } catch (error) {
        console.error("Error fetching images:", error);

        if (error.response?.data?.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = "خطا در ارتباط با سرور و دریافت تصاویر.";
        }
      } finally {
        this.loadingImages = false;
      }
    },

    resetFilters() {
      this.filters = {
        org_id: null,
        manager_ids: [],
        personal_numbers: [],
        camera_ids: [],
        start_date: "",
        end_date: "",
      };
      this.setDefaultDates();
      this.images = [];
      this.hasSearched = false;
      this.errorMessage = null;
      this.currentPage = 1;
      this.totalRows = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.border {
  border: 1px solid #ebe9f1 !important;
}
.cursor-pointer {
  cursor: pointer;
}
.hover-zoom {
  transition: transform 0.2s ease-in-out;
}
.hover-zoom:hover img,
.hover-zoom:hover div {
  transform: scale(1.03);
  transition: transform 0.2s ease-in-out;
}

.square-image-container {
  aspect-ratio: 1 / 1;
}
.object-fit-cover {
  object-fit: cover;
}
</style>
